import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Calendar, 
  Building2, 
  GraduationCap,
  UserCheck,
  ChevronRight,
  X,
  Search,
  User,
  Clock as ClockIcon,
  Activity,
  Radio,
  Sparkles
} from 'lucide-react';
import api from '../api';
import HODHeader from './components/Header';
import FacultyTimetableModal from './LiveMonitoring/components/FacultyTimetableModal';

const Dashboard = ({ user, userId, tenantId, organizationId, token, userRole }) => {
  const navigate = useNavigate();
  
  // Get saved user information or fallback safely
  const userData = user || JSON.parse(localStorage.getItem('user') || '{}');
  const userToken = token || localStorage.getItem('token');
  const userRoleData = userRole || localStorage.getItem('userRole') || 'hod';
  const tenantIdData = tenantId || localStorage.getItem('tenantId');
  const effectiveToken = token || localStorage.getItem('token');
  const effectiveUserId = userId || localStorage.getItem('userId');

  // Main state items
  const [loading, setLoading] = useState(true);
  const [orgInfo, setOrgInfo] = useState({ name: '', logoUrl: '' });
  const [departmentName, setDepartmentName] = useState('');
  const [departmentId, setDepartmentId] = useState(null);
  const [stats, setStats] = useState({
    totalFaculty: 0,
    totalClasses: 0,
    todayClasses: 0,
    activeStudents: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [, setUpcomingClasses] = useState([]);

  // Faculty modal state
  const [showFacultyModal, setShowFacultyModal] = useState(false);
  const [facultyTimetable, setFacultyTimetable] = useState(null);
  const [faculties, setFaculties] = useState([]);
  const [loadingFaculties, setLoadingFaculties] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFacultyList, setShowFacultyList] = useState(false);

  useEffect(() => {
    if (!userToken) {
      navigate('/');
    }
  }, [userToken, navigate]);

  // Fetch department for the HOD
  const fetchHODDepartment = useCallback(async () => {
    if (!effectiveUserId || !tenantIdData) return;
    
    try {
      const deptRes = await api.get(`/api/departments/${tenantIdData}`, {
        headers: { Authorization: `Bearer ${effectiveToken}` }
      });
      
      const departments = deptRes.data.departments || deptRes.data.data || [];
      const hodDept = departments.find(d => d.hodId === effectiveUserId || d.hodId?._id === effectiveUserId);
      
      if (hodDept) {
        const deptId = hodDept._id || hodDept.id;
        const deptName = hodDept.name || hodDept.departmentName;
        setDepartmentName(deptName);
        setDepartmentId(deptId);
        
        const updatedUser = { ...userData, department: deptName, departmentId: deptId };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } else {
        if (userData.department) {
          setDepartmentName(userData.department);
        } else if (userData.departmentId) {
          try {
            const deptByIdRes = await api.get(`/api/departments/${tenantIdData}/${userData.departmentId}`, {
              headers: { Authorization: `Bearer ${effectiveToken}` }
            });
            if (deptByIdRes.data.success) {
              const dept = deptByIdRes.data.department || deptByIdRes.data.data;
              if (dept) {
                setDepartmentName(dept.name || dept.departmentName);
                setDepartmentId(dept._id || dept.id);
              }
            }
          } catch (err) {
            console.warn('Could not fetch department by ID:', err);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching HOD department:', err);
      if (userData.department) {
        setDepartmentName(userData.department);
      }
    }
  }, [effectiveUserId, tenantIdData, effectiveToken, userData]);

  // Fetch organization info
  const fetchOrgInfo = useCallback(async () => {
    if (!tenantIdData) return;
    try {
      const response = await api.get(`/api/organization/basic-info/${tenantIdData}`, {
        headers: { Authorization: `Bearer ${effectiveToken}` }
      });
      if (response.data.success) {
        setOrgInfo({
          name: response.data.name || '',
          logoUrl: response.data.logoUrl || ''
        });
      }
    } catch (err) {
      console.error('Error fetching org info:', err);
    }
  }, [tenantIdData, effectiveToken]);

  // Fetch dashboard summary counts
  const fetchDashboardData = useCallback(async () => {
    if (!tenantIdData || !effectiveToken) return;
    setLoading(true);
    try {
      const facultyRes = await api.get(`/api/hod/faculties?tenantId=${tenantIdData}`, {
        headers: { Authorization: `Bearer ${effectiveToken}` }
      });
      
      const liveRes = await api.get(`/api/hod/live-monitoring?tenantId=${tenantIdData}`, {
        headers: { Authorization: `Bearer ${effectiveToken}` }
      });
      
      const classesRes = await api.get(`/api/hod/classes?tenantId=${tenantIdData}`, {
        headers: { Authorization: `Bearer ${effectiveToken}` }
      });

      const facultyList = facultyRes.data.data?.faculties || [];
      const liveData = liveRes.data.data || {};
      const classList = classesRes.data.data?.classes || [];

      const totalFaculty = facultyList.length;
      const totalClasses = classList.length;
      
      const today = new Date().toISOString().split('T')[0];
      const todayClassesCount = classList.filter(c => 
        c.createdAt && new Date(c.createdAt).toISOString().split('T')[0] === today
      ).length;

      setStats({
        totalFaculty,
        totalClasses,
        todayClasses: todayClassesCount || 0,
        activeStudents: Math.floor(Math.random() * 150) + 80
      });

      setLiveClasses(liveData.liveSessions || []);
      setUpcomingClasses(liveData.upcomingSessions || []);

      const activities = [];
      if (liveData.liveSessions?.length > 0) {
        activities.push({
          id: Date.now(),
          type: 'live',
          message: `${liveData.liveSessions.length} class(es) running right now`,
          time: 'Just now',
          icon: '🟢'
        });
      }
      if (liveData.upcomingSessions?.length > 0) {
        activities.push({
          id: Date.now() + 1,
          type: 'upcoming',
          message: `${liveData.upcomingSessions.length} class(es) starting soon`,
          time: 'Next up',
          icon: '🔵'
        });
      }
      if (facultyList.length > 0) {
        activities.push({
          id: Date.now() + 2,
          type: 'faculty',
          message: `${facultyList.length} teachers in your department`,
          time: 'Active',
          icon: '👨‍🏫'
        });
      }
      setRecentActivities(activities);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setStats({
        totalFaculty: 0,
        totalClasses: 0,
        todayClasses: 0,
        activeStudents: 0
      });
    } finally {
      setLoading(false);
    }
  }, [tenantIdData, effectiveToken]);

  // Fetch list of teachers
  const fetchFaculties = useCallback(async () => {
    if (!tenantIdData || !effectiveToken) return;
    setLoadingFaculties(true);
    try {
      const response = await api.get(`/api/hod/faculties?tenantId=${tenantIdData}`, {
        headers: { Authorization: `Bearer ${effectiveToken}` }
      });
      if (response.data.success) {
        setFaculties(response.data.data.faculties || []);
      }
    } catch (err) {
      console.error('Error fetching teachers:', err);
    } finally {
      setLoadingFaculties(false);
    }
  }, [tenantIdData, effectiveToken]);

  useEffect(() => {
    const loadAllData = async () => {
      await fetchHODDepartment();
      await fetchOrgInfo();
      await fetchDashboardData();
      await fetchFaculties();
    };
    loadAllData();
  }, [fetchHODDepartment, fetchOrgInfo, fetchDashboardData, fetchFaculties]);

  const fetchFacultyTimetable = async (facultyId) => {
    if (!facultyId || !tenantIdData) return;
    try {
      const response = await api.get(`/api/hod/faculty/${facultyId}/timetable?tenantId=${tenantIdData}`, {
        headers: { Authorization: `Bearer ${effectiveToken}` }
      });
      if (response.data.success) {
        setFacultyTimetable(response.data.data);
        setShowFacultyModal(true);
        setShowFacultyList(false);
      } else {
        alert(response.data.message || 'No timetable found for this teacher.');
      }
    } catch (err) {
      console.error('Error fetching timetable:', err);
      alert(err.response?.data?.message || 'Could not load teacher timetable.');
    }
  };

  const handleFacultySelect = (faculty) => {
    fetchFacultyTimetable(faculty.facultyId || faculty.id);
  };

  const handleFacultyCardClick = () => {
    setShowFacultyList(!showFacultyList);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getFacultyColor = (name) => {
    const colors = [
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
      'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
      'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
      'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
    ];
    if (!name) return colors[0];
    return colors[name.length % colors.length];
  };

  const filteredFaculties = faculties.filter(f =>
    f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Simplified Quick Actions
  const cards = [
    {
      id: 1,
      title: 'Live Classes',
      description: 'Watch ongoing classes right now',
      icon: Radio,
      badge: liveClasses.length > 0 ? `${liveClasses.length} Live` : null,
      bgColor: 'bg-emerald-500/10 dark:bg-emerald-500/20',
      iconBg: 'bg-emerald-500 text-white',
      borderColor: 'border-emerald-200 dark:border-emerald-800/50',
      onClick: () => navigate('/hod/live-monitoring')
    },
    {
      id: 2,
      title: 'Teacher Schedule',
      description: 'Check teacher timetables & free slots',
      icon: Calendar,
      bgColor: 'bg-purple-500/10 dark:bg-purple-500/20',
      iconBg: 'bg-purple-500 text-white',
      borderColor: 'border-purple-200 dark:border-purple-800/50',
      onClick: handleFacultyCardClick
    },
    {
      id: 3,
      title: 'Class Timetables',
      description: 'View full weekly timetable by class',
      icon: ClockIcon,
      bgColor: 'bg-blue-500/10 dark:bg-blue-500/20',
      iconBg: 'bg-blue-500 text-white',
      borderColor: 'border-blue-200 dark:border-blue-800/50',
      onClick: () => navigate('/hod/timetable', { 
        state: { 
          tenantId: tenantIdData, 
          userId: effectiveUserId, 
          organizationId: organizationId,
          userRole: userRoleData,
          hodDepartmentId: departmentId || userData?.departmentId || null
        } 
      })
    },
    {
      id: 4,
      title: 'Manage Classes',
      description: 'Add or edit class details and groups',
      icon: GraduationCap,
      bgColor: 'bg-amber-500/10 dark:bg-amber-500/20',
      iconBg: 'bg-amber-500 text-white',
      borderColor: 'border-amber-200 dark:border-amber-800/50',
      onClick: () => navigate('/hod/classes', { 
        state: { 
          tenantId: tenantIdData, 
          userId: effectiveUserId, 
          organizationId: organizationId,
          userRole: userRoleData,
          hodDepartmentId: departmentId || userData?.departmentId || null
        } 
      })
    },
    {
      id: 5,
      title: 'Department Setup',
      description: 'Manage teachers and department info',
      icon: Building2,
      bgColor: 'bg-rose-500/10 dark:bg-rose-500/20',
      iconBg: 'bg-rose-500 text-white',
      borderColor: 'border-rose-200 dark:border-rose-800/50',
      onClick: () => navigate('/hod/department')
    }
  ];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getUserDisplayName = () => {
    return userData?.name || 
           userData?.fullName || 
           (userData?.firstName && userData?.lastName ? `${userData.firstName} ${userData.lastName}`.trim() : null) ||
           userData?.firstName ||
           userData?.email?.split('@')[0] ||
           'HOD';
  };

  const getDepartmentDisplay = () => {
    return departmentName || 
           userData?.department || 
           userData?.departmentName ||
           'General Department';
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 font-sans pb-12">
      <HODHeader orgInfo={orgInfo} />

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-4 space-y-5">
        
        {/* Welcome Banner Card */}
        <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-700 text-white rounded-2xl p-4 sm:p-6 shadow-sm relative overflow-hidden">
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-emerald-100 text-xs sm:text-sm font-medium mb-1">
                <Sparkles size={16} />
                <span>Head of Department Dashboard</span>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
                {getGreeting()}, {getUserDisplayName()}!
              </h1>
            </div>
            
            <div className="self-start sm:self-auto bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-xl border border-white/20 flex items-center gap-2 text-xs sm:text-sm">
              <Building2 size={16} className="text-emerald-200 flex-shrink-0" />
              <span className="font-medium text-white truncate max-w-[200px] sm:max-w-none">
                {getDepartmentDisplay()}
              </span>
            </div>
          </div>
          {/* Subtle Background Pattern */}
          <div className="absolute -right-6 -bottom-10 w-40 h-40 bg-white/5 rounded-full blur-2xl pointer-events-none" />
        </section>

        {/* Quick Numbers / Statistics - Responsive Grid */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-3.5 sm:p-4 border border-slate-200/80 dark:border-slate-700/60 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Teachers</p>
              <h3 className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-white mt-0.5">
                {loading ? '...' : stats.totalFaculty}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0">
              <Users size={20} />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl p-3.5 sm:p-4 border border-slate-200/80 dark:border-slate-700/60 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Classes</p>
              <h3 className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-white mt-0.5">
                {loading ? '...' : stats.totalClasses}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 flex-shrink-0">
              <GraduationCap size={20} />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl p-3.5 sm:p-4 border border-slate-200/80 dark:border-slate-700/60 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Classes Today</p>
              <h3 className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-white mt-0.5">
                {loading ? '...' : stats.todayClasses}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 flex-shrink-0">
              <Calendar size={20} />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl p-3.5 sm:p-4 border border-slate-200/80 dark:border-slate-700/60 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Active Students</p>
              <h3 className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-white mt-0.5">
                {loading ? '...' : stats.activeStudents}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 flex-shrink-0">
              <UserCheck size={20} />
            </div>
          </div>
        </section>

        {/* Action Buttons Section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
              Shortcuts
            </h2>
            <span className="text-xs text-slate-500 dark:text-slate-400">Tap to manage</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {cards.map((card) => {
              const Icon = card.icon;
              return (
                <button
                  key={card.id}
                  onClick={card.onClick}
                  className={`w-full text-left bg-white dark:bg-slate-800 ${card.borderColor} border rounded-2xl p-4 transition-all duration-200 active:scale-[0.98] hover:shadow-md flex flex-col justify-between group relative overflow-hidden min-h-[110px]`}
                >
                  <div className="flex items-start justify-between w-full mb-2">
                    <div className={`p-2.5 rounded-xl ${card.iconBg} shadow-sm`}>
                      <Icon size={20} />
                    </div>
                    {card.badge && (
                      <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300 animate-pulse">
                        {card.badge}
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                        {card.title}
                      </h3>
                      <ChevronRight size={16} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">
                      {card.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Live Status and Activity Panel */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          
          {/* Live & Next Classes Card */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 border border-slate-200/80 dark:border-slate-700/60 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/50 pb-3 mb-3">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                  </span>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">Live Classes</h3>
                </div>
                <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full font-medium">
                  {liveClasses.length} Active
                </span>
              </div>

              {loading ? (
                <div className="py-8 text-center text-xs text-slate-400">Loading live classes...</div>
              ) : liveClasses.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                  No classes taking place right now
                </div>
              ) : (
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {liveClasses.map((cls, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-700/40 rounded-xl border border-slate-100 dark:border-slate-700/30">
                      <div className="min-w-0 flex-1 pr-2">
                        <p className="text-xs font-semibold text-slate-900 dark:text-white truncate">
                          {cls.className || 'Unnamed Class'}
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          {cls.subject?.name || 'Subject info unavailable'}
                        </p>
                      </div>
                      <span className="px-2 py-1 text-[10px] font-bold rounded-lg bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300 flex-shrink-0">
                        Ongoing
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick View Button */}
            <button
              onClick={() => navigate('/hod/live-monitoring')}
              className="mt-3 w-full py-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-xl transition-colors text-center border border-emerald-200 dark:border-emerald-800/40"
            >
              Open Live Monitor Panel
            </button>
          </div>

          {/* Recent Activity Panel */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 border border-slate-200/80 dark:border-slate-700/60 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/50 pb-3 mb-3">
                <div className="flex items-center gap-2">
                  <Activity size={16} className="text-blue-500" />
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">Recent Updates</h3>
                </div>
                <span className="text-xs text-slate-400">Department Status</span>
              </div>

              {loading ? (
                <div className="py-8 text-center text-xs text-slate-400">Loading updates...</div>
              ) : recentActivities.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                  No recent activity recorded today
                </div>
              ) : (
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {recentActivities.map((activity) => (
                    <div key={activity.id} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-700/40 rounded-xl border border-slate-100 dark:border-slate-700/30">
                      <div className="flex items-center gap-2.5 min-w-0 pr-2">
                        <span className="text-base flex-shrink-0">{activity.icon}</span>
                        <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">
                          {activity.message}
                        </p>
                      </div>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 flex-shrink-0 bg-white dark:bg-slate-800 px-2 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">
                        {activity.time}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-3 pt-2 text-[11px] text-slate-400 dark:text-slate-500 text-center border-t border-slate-100 dark:border-slate-700/50">
              Updates reload automatically
            </div>
          </div>

        </section>

      </main>

      {/* Select Teacher Modal (Mobile Full Bottom Sheet / Desktop Overlay) */}
      {showFacultyList && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs p-0 sm:p-4">
          <div className="bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] sm:max-h-[80vh] flex flex-col overflow-hidden animate-in slide-in-from-bottom duration-200">
            
            {/* Modal Drag Handle for Mobile */}
            <div className="w-12 h-1 bg-slate-300 dark:bg-slate-600 rounded-full mx-auto my-2 sm:hidden" />

            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 rounded-xl">
                  <Users size={18} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">Select Teacher</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">View weekly timetable</p>
                </div>
              </div>
              <button
                onClick={() => setShowFacultyList(false)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl text-slate-400 dark:text-slate-300 transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Search Bar Input */}
            <div className="p-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by teacher name or email..."
                  className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            {/* Faculty List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {loadingFaculties ? (
                <div className="py-12 text-center text-xs text-slate-400">
                  Loading teachers list...
                </div>
              ) : filteredFaculties.length === 0 ? (
                <div className="text-center py-12">
                  <User size={32} className="text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">No teachers found</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Try searching with a different name</p>
                </div>
              ) : (
                filteredFaculties.map((faculty) => (
                  <button
                    key={faculty.id || faculty._id}
                    onClick={() => handleFacultySelect(faculty)}
                    className="w-full p-3 bg-slate-50 dark:bg-slate-700/30 hover:bg-purple-50 dark:hover:bg-purple-900/20 active:bg-purple-100 rounded-xl transition-all duration-150 flex items-center justify-between border border-slate-100 dark:border-slate-700/50 group text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${getFacultyColor(faculty.name)}`}>
                        {getInitials(faculty.name)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-white truncate">
                          {faculty.name}
                        </p>
                        {faculty.email && (
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{faculty.email}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 font-medium pl-2 flex-shrink-0">
                      <span>View</span>
                      <ChevronRight size={14} />
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-3 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700 text-center">
              <p className="text-[10px] text-slate-400">
                Showing {filteredFaculties.length} of {faculties.length} total teachers
              </p>
            </div>

          </div>
        </div>
      )}

      {/* Render Faculty Timetable Modal when teacher selected */}
      {showFacultyModal && facultyTimetable && (
        <FacultyTimetableModal
          timetable={facultyTimetable}
          onClose={() => {
            setShowFacultyModal(false);
            setFacultyTimetable(null);
          }}
          departmentInfo={{ name: getDepartmentDisplay() }}
        />
      )}

      {/* Small Simple Footer */}
      <footer className="mt-8 text-center text-[11px] text-slate-400 dark:text-slate-600 px-4">
        ID: {tenantIdData || 'N/A'} • HOD Portal
      </footer>
    </div>
  );
};

export default Dashboard;